import React, { useEffect } from "react";

import { ISearchParamsAware, IPaginationAware } from "./ISearchParams";
import { range } from "../utils";

import "./TermsPagination.css";
import "bootstrap/dist/css/bootstrap.css";

interface ITermsPagination extends ISearchParamsAware, IPaginationAware {}

interface IPageSwitch extends ITermsPagination {
  offset: number;
  text: string;
  disabled: boolean;
}

const splitWithNeighbors = (
  pivot: number,
  neighborsCount: number,
  maxUpperBound: number
) => {
  const floored = Math.floor(pivot);
  const lowerBound = Math.max(0, floored - neighborsCount);
  const upperBound = Math.min(maxUpperBound, floored + neighborsCount);
  return range(lowerBound, upperBound, 1);
};

const splitIntoThirds = (
  lower: number,
  center: number,
  higher: number,
  neighborsCount: number,
  maxUpperBound: number
) => {
  return [
    splitWithNeighbors(lower, neighborsCount, maxUpperBound),
    splitWithNeighbors(center, neighborsCount, maxUpperBound),
    splitWithNeighbors(higher, neighborsCount, maxUpperBound)
  ];
};

const mergePageGroups = (groups: number[][]) => {
  let merged = groups.shift();
  if (!merged) return [];

  for (let group of groups) {
    while (group.length > 0 && group[0] <= merged[merged.length - 1]) {
      group.shift();
    }
    merged.push(...group);
  }

  return merged;
};

const makePageGroups = (
  neighborsCount: number,
  currentPage: number,
  totalPages: number
) => {
  let pageGroups = [range(1, 1 + neighborsCount, 1)];

  if (
    currentPage > neighborsCount + 1 &&
    currentPage < totalPages - neighborsCount
  ) {
    // current page has its own center group
    if (currentPage === totalPages / 2) {
      pageGroups.push(
        ...splitIntoThirds(
          currentPage / 2,
          currentPage,
          (currentPage / 2) * 3,
          neighborsCount,
          totalPages
        )
      );
    } else if (currentPage < totalPages / 2) {
      const third = (totalPages - currentPage) / 3;
      pageGroups.push(
        ...splitIntoThirds(
          currentPage,
          currentPage + third,
          currentPage + 2 * third,
          neighborsCount,
          totalPages
        )
      );
    } else {
      const third = currentPage / 3;
      pageGroups.push(
        ...splitIntoThirds(
          currentPage - 2 * third,
          currentPage - third,
          currentPage,
          neighborsCount,
          totalPages
        )
      );
    }
  } else {
    // divide the centre groups evenly
    pageGroups.push(
      ...splitIntoThirds(
        totalPages / 4,
        totalPages / 2,
        (totalPages / 4) * 3,
        neighborsCount,
        totalPages
      )
    );
  }

  pageGroups.push(range(totalPages - neighborsCount, totalPages, 1));

  return mergePageGroups(pageGroups);
};

const PageSwitch: React.FC<IPageSwitch> = props => {
  const { offset, text, disabled, searchParams, setSearchParams } = props;
  const updatedParams = { ...searchParams, offset: offset };

  const className = disabled ? "page-item disabled" : "page-item";

  // TODO cleaner interface

  return (
    <li className={className}>
      <button
        className="page-link btn"
        onClick={() => setSearchParams(updatedParams)}
      >
        {text}
        {/* { page == currentPage && (<span> (current)</span>)} */}
      </button>
    </li>
  );
};

const TermsPagination: React.FC<ITermsPagination> = props => {
  const { searchParams, totalPages, currentPage } = props;

  // TODO cleaner way to handle rerender
  useEffect(() => {}, [props.currentPage]);

  if (totalPages <= 1) return (<span/>)

  const groupSize = 3;
  const neighborsCount = Math.floor(groupSize / 2);
  const pages = makePageGroups(neighborsCount, currentPage, totalPages);

  // TODO move to separate func
  let paginationElements = [
    <PageSwitch
      {...props}
      offset={(currentPage - 2) * searchParams.limit}
      text="&laquo;"
      disabled={currentPage === 1}
    />
  ];

  for (let i = 0; i < pages.length; i++) {
    const page = pages[i];

    paginationElements.push(
      <PageSwitch
        disabled={false}
        offset={(page - 1) * searchParams.limit}
        text={page.toString()}
        {...props}
      />
    );

    if (i < pages.length - 1 && pages[i + 1] > page + 1) {
      paginationElements.push(<li className="page-item">...</li>);
    }
  }

  paginationElements.push(
    <PageSwitch
    disabled={currentPage === totalPages}
    offset={currentPage * searchParams.limit}
    text="&raquo;"
    {...props}
    />
  );

  return (
    <ul className="pagination justify-content-center">
      {paginationElements.map(element => element)}
    </ul>
  );
};

export default TermsPagination;
